import React, { Dispatch, useState, useEffect, MouseEvent, useRef, useLayoutEffect } from 'react';
import '../App.css';
import Turn from './Turn';
import MiniTimeline from './MiniTimeline';
import ReactGridLayout from './ReactGridLayout';
import { IInject, IScenario, ITurnBrief } from '../interfaces';
import { DebouncedFunc } from 'lodash';
import { Launch as LaunchIcon } from '@carbon/icons-react'
import { TurnBrief } from '../Classes1';

interface TurnTimelineProps {
    updateShowHide: Dispatch<any>;
    setInjectComponents: Dispatch<any>;
    injectComponents: IInject[];
    showTimeline: string;
    showNodeInjMenu: string;
    showMiniTimeline: string;
    setInjectItem: Dispatch<any>;
    injectItem: IInject;
    clickBranches: Dispatch<any>; 
    gameTimeInterval: number;
    setZoomLevel: Dispatch<any>;
    zoomLevel: number;
    scenSnippets: ITurnBrief[];
    setSnippetModalIsOpen: Dispatch<any>;
    setSelectedSnippet: Dispatch<any>;
    setSelectedScenario: Dispatch<any>;
    setNewSnippet: Dispatch<any>;
    selectedScenario: IScenario;
    showRGLTimeline: string;
    saveLayouts: any;
    setSaveLayouts: Dispatch<any>;
    setLastUserClick: Dispatch<any>;
    lastUserClick: string;
    createInjectComponents: Dispatch<any>;
    saveScenario: (i: IInject[]) => Promise<void>;
    onChangeSave: DebouncedFunc<any>;
    openDeleteModal: () => void;
    onError: Dispatch<string>;
  }

const level5width: number = 180;
const level4width: number = 358;

const level5padding: number = 130;
const level4padding: number = 130;

const miniTimelineMax: number = 310;
const miniTimelineWidth: number = 296

const TurnTimeline: React.FC<TurnTimelineProps> = ({ setInjectComponents, injectComponents, updateShowHide, showTimeline, showNodeInjMenu, showMiniTimeline, setInjectItem, injectItem, clickBranches, gameTimeInterval, zoomLevel, setZoomLevel, scenSnippets, setSnippetModalIsOpen, setSelectedSnippet, selectedScenario, setSelectedScenario, showRGLTimeline, saveLayouts, setSaveLayouts, setLastUserClick, lastUserClick, createInjectComponents, setNewSnippet, saveScenario, onChangeSave, openDeleteModal, onError }) => {
   
    const [timelineX, setTimelineX] = useState<number>(0);
    const [miniLeft, setMiniLeft] = useState<{x: number, y: number} | undefined>({x: 0, y: 0});
    const [windowWidth, setWindowWidth] = useState<number>(0);
    const [addTurnLeft, setAddTurnLeft] = useState<number>(0);
    // we don't need to start calculating a multiplier until the 6th turn
    const [multiplier, setMultiplier] = useState<number>(6);
    const [showTurn, setShowTurn] = useState<string>('none');
    const [turnLength, setTurnLength] = useState<number>(0);
    const [turnPadding, setTurnPadding] = useState<number>(0);

    const scrollAreaUI = useRef<HTMLDivElement>(null);
    const [recentlyAddedTurn, setRecentlyAddedTurn] = useState<number>(-1);
    useLayoutEffect(() => {
        if (recentlyAddedTurn > -1) {
            setTimeout(() => {
                scrollAreaUI?.current?.parentElement?.scrollTo({
                    left: scrollAreaUI?.current?.scrollWidth,
                    behavior: 'smooth',
                });    
            }, 200)
            setRecentlyAddedTurn(-1);
        }
    }, [recentlyAddedTurn]);
    let turnWrapper: string;
    let turnTimelineLeft: string = '';
    let turnContentLeft = (timelineX * -3.83) + 'px';

    turnWrapper = gameTimeInterval < 10 ? gameTimeInterval * turnLength + 450 + 'px' : gameTimeInterval * turnLength + 180 + 'px'

    if (showNodeInjMenu === 'show') {
        turnTimelineLeft = 'timeline-left'
    }

    useEffect(() => {
        if (zoomLevel === 5) {
            setTurnLength(level5width);
            setTurnPadding(level5padding);
          } else {
            setTurnLength(level4width);
            setTurnPadding(level4padding);
          }
    }, [zoomLevel]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setTimeout(() => {
            if (showTurn === 'none') {
                setShowTurn('block')
            }
        }, 1000)
    }, [setShowTurn, showTurn])

    const calcMultiplier = () => {
        // 1.4 is the number of times larger the left px of the large timeline is than the left px of the mini timeline 
        let multiplierValue: number = gameTimeInterval * (gameTimeInterval < 6 ? 1.6 : 1.4);
        setMultiplier(multiplierValue)
    }

    useEffect(() => {
        if (gameTimeInterval > 1) {
            calcMultiplier()
        }
    }, [gameTimeInterval, zoomLevel]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const turnsContent = document.querySelector(".turn-content") as HTMLElement;
        
        turnsContent.scrollLeft = timelineX * multiplier
        
        setAddTurnLeft((gameTimeInterval * turnLength) + turnPadding);
    }, [timelineX, gameTimeInterval, zoomLevel, turnLength, turnPadding]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const turnsContent = document.querySelector(".turn-content") as HTMLElement;
        
        turnsContent!.addEventListener("scroll", (event) => {
            let miniLeft: number = 0;
            
            miniLeft = turnsContent!.scrollLeft / multiplier;
            
            if ((miniLeft + windowWidth) < miniTimelineMax) {
                if (windowWidth !== 0 && windowWidth !== miniTimelineWidth) {
                    setMiniLeft({x: miniLeft, y: 0})
                }
            }
          
        });
    }, [windowWidth]) // eslint-disable-line react-hooks/exhaustive-deps

    const addTurn = () => {
        setSelectedScenario({
            ...selectedScenario, 
            gameTimeInterval: selectedScenario.gameTimeInterval+1
        });
        setRecentlyAddedTurn(selectedScenario.gameTimeInterval + 1);
    }

    const deleteTurn = () => {
        setSelectedScenario({
            ...selectedScenario, 
            gameTimeInterval: selectedScenario.gameTimeInterval-1
        });
    }

    const showDeleteBtn = (turnNum: number) => {
        if (turnNum === 1) return;
        if (selectedScenario.published) return;
        // Check if the turn is occupied by an inject
        if (injectComponents.some(inject => turnNum <= (inject.startTurn + inject.turnLength - 1))) return;

        return <img className="delete-turn" src="img/trash-can.svg" onClick={deleteTurn} alt="trash"/>
    }

    const hasDeleteBtn = (turnNum: number) => {
        if (selectedScenario.published) return

        // Check if the turn is occupied by an inject
        if (injectComponents.some(inject => turnNum <= (inject.startTurn + inject.turnLength - 1))) return false;

        return true
    }

    const clickSnippet = (ev: MouseEvent) => {

        ev.stopPropagation();
  
        const target = ev.currentTarget as HTMLElement;
      
        if (target) { 
          setSnippetModalIsOpen(true)
          setSelectedSnippet(parseInt(target.dataset.turn!))
          const turn: number = parseInt(target.dataset.turn!)

          if (!selectedScenario.snippets.some((snippet) => snippet.turn === turn)) {
            const newSnip: ITurnBrief = new TurnBrief();
            newSnip.turn = parseInt(target.dataset.turn!)
            const newSnippets: ITurnBrief[] = JSON.parse(JSON.stringify(selectedScenario.snippets))
            newSnippets.push(newSnip)
            setNewSnippet(newSnip)
            setSelectedScenario({
                ...selectedScenario, 
                snippets: newSnippets
            });
          } else {
            setNewSnippet(selectedScenario.snippets.filter((snippet) => snippet.turn === turn)[0])
          }
          
        }
      }

    return (
        <div>
            <div className={`turn-content ${showTimeline} ${turnTimelineLeft}`}>
                <div className="turns-wrapper" ref={scrollAreaUI} style={{'width':turnWrapper, 'left': turnContentLeft }}>

                <ReactGridLayout display='block' onChangeSave={onChangeSave} showRGLTimeline={showRGLTimeline} injectComponents={injectComponents} gameTimeInterval={selectedScenario.gameTimeInterval} updateShowHide={updateShowHide} isSelected={''} setInjectComponents={setInjectComponents} setInjectItem={setInjectItem}  clickBranches={clickBranches} id={'1'} key={1} zoomLevel={zoomLevel} setZoomLevel={setZoomLevel} scenSnippets={scenSnippets} setNewSnippet={setNewSnippet} setSnippetModalIsOpen={setSnippetModalIsOpen} setSelectedSnippet={setSelectedSnippet} setSelectedScenario={setSelectedScenario} selectedScenario={selectedScenario} saveLayouts={saveLayouts} setSaveLayouts={setSaveLayouts} lastUserClick={lastUserClick} setLastUserClick={setLastUserClick} injectItem={injectItem} saveScenario={saveScenario} createInjectComponents={createInjectComponents} openDeleteModal={openDeleteModal} onError={onError} />
                    <div className="turn-gutter">
                        <div className="turn-start-scenario">
                            <span>Scenario Start</span>
                        </div>
                    </div>
                    {Array.from({length: gameTimeInterval}, (item, index) => 
                        <div key={index} className="turn-wrapper" style={{ width: turnLength + 'px'}}>
                            {showDeleteBtn(index+1)}
                            <div className={`turn-brief-label ${hasDeleteBtn(index+1) ? 'has-delete' : ''}`} onClick={clickSnippet} data-turn={index+1}>
                                <span>Brief</span>
                                <LaunchIcon color="#FFFFFF"/>
                            </div>
                            <Turn updateShowHide={updateShowHide} setInjectComponents={setInjectComponents} injectComponents={injectComponents} setInjectItem={setInjectItem} clickBranches={clickBranches} injectItem={injectItem} turn={index+1} gameTimeInterval={gameTimeInterval} greedy={false} zoomLevel={zoomLevel} setZoomLevel={setZoomLevel} scenSnippets={scenSnippets} setSnippetModalIsOpen={setSnippetModalIsOpen} setSelectedSnippet={setSelectedSnippet} selectedScenario={selectedScenario} />
                        </div>
                    )}
                    
                </div>
                { selectedScenario.gameTimeInterval < 10 && !selectedScenario.published ? <div className="add-turn" onClick={addTurn} style={{ display: showTurn, left: addTurnLeft + 'px' }}>
                    <div className="add-turn-text">Add a Turn +</div>
                </div> : <></> }
            </div>
            <MiniTimeline miniLeft={miniLeft} injectComponents={injectComponents} setTimelineX={setTimelineX} showMiniTimeline={showMiniTimeline} gameTimeInterval={selectedScenario.gameTimeInterval} saveLayouts={saveLayouts} setZoomLevel={setZoomLevel} zoomLevel={zoomLevel} windowWidth={windowWidth} setWindowWidth={setWindowWidth} setMiniLeft={setMiniLeft} />
        </div>
    )
}

export default TurnTimeline;